import React from 'react';
import { Dialog } from '@headlessui/react';
import type { ComponentProps, ForwardedRef } from 'react';

type DialogProps = ComponentProps<typeof Dialog>;

type THDialogWrapperProps = Omit<DialogProps, 'ref'> & {
    innerRef: ForwardedRef<HTMLDivElement>;
};

/**
 * Wraps Headless UI's `Dialog` so that only the dialog actually on top responds
 * to a dismiss — otherwise closing a stacked dialog would close the one behind
 * it too.
 *
 * "On top" means the last OPEN one, not the last mounted one. It used to mean
 * the latter, which broke dismissal wherever a dialog is rendered per list
 * item: `QuickViewModal` mounts inside every `ProductTile`, so a search page
 * holds two dozen instances and only the final tile's could ever close. Every
 * other tile's quick view ignored both the backdrop and Escape — the X still
 * worked, because it calls onClose directly rather than going through Dialog.
 *
 * Openness is read off the DOM rather than an `open` prop: all but one of these
 * modals are driven by a `Transition` and never pass `open` at all, and a
 * closed one unmounts, so a connected element is the honest signal.
 */
class THDialogWrapper extends React.Component<THDialogWrapperProps> {
    /**
     * Mount order, each with the node it rendered. The node is what says whether
     * that dialog is showing: connected to the document exactly while it is.
     */
    static instances: { comp: THDialogWrapper, el: HTMLDivElement | null }[] = [];

    constructor(props: THDialogWrapperProps) {
        super(props);
        THDialogWrapper.instances.push({ comp: this, el: null });
    }

    componentWillUnmount(): void {
        THDialogWrapper.instances = THDialogWrapper.instances.filter(({ comp }) => comp !== this);
    }

    /** Keep our own handle on the node while still honouring the caller's ref. */
    setRef = (node: HTMLDivElement | null) => {
        const entry = THDialogWrapper.instances.find(({ comp }) => comp === this);
        if (entry) entry.el = node;
        const { innerRef } = this.props;
        if (typeof innerRef === 'function') {
            innerRef(node);
        } else if (innerRef) {
            // eslint-disable-next-line no-param-reassign
            (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
    };

    render() {
        const { innerRef, onClose, ...rest } = this.props;
        return (
            <Dialog
                {...rest}
                ref={this.setRef}
                onClose={(value) => {
                    const open = THDialogWrapper.instances.filter(({ el }) => !!el?.isConnected);
                    if (open.length > 0 && open[open.length - 1].comp !== this) {
                        return;
                    }
                    onClose(value);
                }} />
        );
    }
}

export default React.forwardRef<HTMLDivElement, Omit<DialogProps, 'ref'>>((props, ref) => <THDialogWrapper innerRef={ref} {...props} />);
